import {put, call} from 'redux-saga/effects';
import Contact from "../../entities/contact";
import * as Actions from "../actions/contactActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type ContactAction = {
    contactId: string;
    contact: Contact;
    contacts: Contact[];
};

export function* fetchOneContact(action: ContactAction) {
	try {
		const contact: Contact = yield call(api.findOne, 'contact', action.contactId);

		// Now we can update the user interface
		yield put({type: Actions.CONTACT_FETCH_ONE_SUCCESS, contactId: action.contactId, contact});
	}
	catch(err) {
		yield put({type: Actions.CONTACT_FETCH_ONE_FAILURE, contactId: action.contactId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {ContactAction} action
 */
export function* saveContact(action: ContactAction) {
    // console.debug('⮂ CONTACT_SAVE saga', action.contact?.id);
    
	if (action.contact === undefined || action.contact === null) {
		console.error('saveContact received a null');
		throw new Error('saveContact received a null');
	}

	if (!(action.contact instanceof Contact)) {
		action.contact = new Contact(action.contact);
	}

	try {
		// Save to IndexedDB
		const addResult = yield call(idb.add, 'contact', action.contact);
		if (addResult.isErr()) {
			console.error(`⮂ saveContact: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.CONTACT_SAVE_FAILURE, contact: action.contact, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.CONTACT_SAVE, contact: action.contact});

		// Delete this entry from the cache (if there is one). Use uncacheByEntityId so we
		// catch entries regardless of which parentId the writer used (zero for grid views,
		// topLevelId for sub-task views, etc.) — the prior uncacheItem call passed entityId
		// as parentId, which never matched what writers store under, so entries leaked.
		yield call(window.storageWorker.uncacheByEntityId, getCurrentFirmId(), getCurrentUserId(), 'contact', action.contact.id);

		// Let the queue know to process
		yield call(processQueue);
	}
	catch(err) {
		yield put({type: Actions.CONTACT_SAVE_FAILURE, contact: action.contact, status: 500, err});

		console.error('Error saving to database', err);
	}
}

/**
 * TODO: Update for saveMany
 * @param {ContactAction} action
 * @param {Contact[]} action.contacts
 */
export function* saveManyContacts(action: ContactAction) {
    // console.debug('⮂ CONTACT_SAVE_MANY saga', action.contacts.map(contact => contact.id).join(','));
    
    if (!Array.isArray(action.contacts)) {
        console.error('saveManyContacts expected an array, received', action.contacts);
        throw new Error('saveManyContacts expected an array');
    }

	try {
		const toSave: Contact[] = [];

		// Save each item to IndexedDB
		for(let i = 0; i < action.contacts.length; i++) {
			const contact = action.contacts[i];
			contact.persisted = false;
			toSave.push(contact);
		}

		const addResult = yield call(idb.add, 'contact', toSave);
		if (addResult.isErr()) {
			console.error(`⮂ saveManyContacts: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.CONTACT_SAVE_MANY_FAILURE, contacts: action.contacts, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.CONTACT_SAVE_MANY, contacts: toSave});

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		yield put({type: Actions.CONTACT_SAVE_MANY_FAILURE, contacts: action.contacts, status: 500, err});

		console.error(`Error saving ${action.contacts.length} contacts to database`, err);
	}
}

/**
 * @param {ContactAction} action
 */
export function* removeContact(action: ContactAction) {
    const userId = getCurrentUserId();
	if (!userId || action.contact === undefined || action.contact === null || !(action.contact instanceof Contact)) {
			console.error('removeContact expected type Contact, received', action.contact);
			throw new Error('removeContact expected type Contact');
		}

	try {
		// Mark as deleted
		action.contact.status = 'D';
		action.contact.addedOn = newHRTimestamp();
		action.contact.addedBy = userId;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'contact', action.contact);
		if (addResult.isErr()) {
			console.error(`⮂ removeContact: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.CONTACT_REMOVE_FAILURE, contact: action.contact, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.CONTACT_REMOVE, contact: action.contact});

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		yield put({type: Actions.CONTACT_REMOVE_FAILURE, contact: action.contact, status: 500, err});

		console.error('Error deleting from database', err);
	}
}

/**
	* Restore the last version of this Contact.
	*
	* @param {ContactAction} action
	*/
export function* restoreContact(action: ContactAction) {
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreContact expected getCurrentUserId() to return a value');
        return;
    }
    
	if (action.contact === undefined || action.contact === null) {
        console.error('restoreContact expected a Contact, received null');
        throw new Error('restoreContact expected a Contact, received null');
    }
    else if (!(action.contact instanceof Contact)) action.contact = new Contact(action.contact); 
    
	try {
		// Mark as restored
		action.contact.status = 'U';
		action.contact.addedOn = newHRTimestamp();
		action.contact.addedBy = userId;
		action.contact.restore = true;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'contact', action.contact);
		if (addResult.isErr()) {
			console.error(`⮂ restoreContact: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.CONTACT_RESTORE_FAILURE, contact: action.contact, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.CONTACT_RESTORE, contact: action.contact});
	}
	catch(err) {
		yield put({type: Actions.CONTACT_RESTORE_FAILURE, contact: action.contact, status: 500, err});

		console.error('Error restoring from database', err);
	}
}